import React, { useMemo, useState } from 'react';

import {
    Checkbox,
    ListItemText,
    Box,
    MenuItem,
    Grid,
    Skeleton,
    Table,
    TableRow,
    TableCell,
    TableBody
} from '@mui/material';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { useContainer } from 'unstated-next';

import UserContainer from './UserContainer';
import UserTimelineClassSessionRow from './UserTimelineClassSessionRow';
import UserToolbar from './UserToolbar';
import { StyledRoleSelect } from '../../../../../styles/input-row';
import AdminAuthContainer from '../../../AdminAuthContainer';

const Spacer = styled.div`
    margin: 1rem;
`;

const PaddedSkeleton = styled(Skeleton)`
    margin: 0.5rem 0;
`;

const ClassSessionHeatmap = styled(Box)`
    width: 100%;
`;

export const UserDetailActivity = () => {
    const { t } = useTranslation('portal');
    const { userData } = useContainer(UserContainer);
    const { authMode } = useContainer(AdminAuthContainer);
    console.log('ClassSessionDetailActivity userData', userData, 'authMode', authMode);

    const [searchText, setSearchText] = useState('');
    const [actionFilter, setActionFilter] = useState<string[]>([
        'ATTENDANCE',
        'IS_TALKING',
        'PUBLIC_CHAT',
        'PRIVATE_CHAT',
        'RAISED_HAND',
        'FOCUSED',
        'FEEDBACK',
        'REACTION',
        'STARS'
    ]);
    console.log('actionFilter', actionFilter);
    const [showInactiveClassSessions, setShowInactiveClassSessions] = useState<boolean>(true);
    console.log('showInactiveClassSessions', showInactiveClassSessions);

    const filteredClassSessions = useMemo(
        () =>
            userData!.usageEvents?.filter((classSession) => {
                const includedInClassName = classSession.class.class_name
                    .toLocaleLowerCase()
                    .includes(searchText.toLocaleLowerCase());
                const includedInDate = classSession.class_session.date_str
                    .toLocaleLowerCase()
                    .includes(searchText.toLocaleLowerCase());
                return includedInClassName || includedInDate;
            }),
        [searchText, userData]
    );

    const handleChangeSearch = (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
        setSearchText(event.target.value);
    };

    const handleActionFilter = (action: string[]) => {
        setActionFilter(action);
    };
    const handleActionFilterChange = (event) => {
        console.log('handleActionFilterChange', event);
        handleActionFilter(event.target.value);
    };

    const handleShowInactiveClassSessions = (value: boolean) => {
        setShowInactiveClassSessions(value);
    };
    const handleShowInactiveClassSessionsChange = (event) => {
        console.log('handleShowInactiveClassSessionsChange', event);
        handleShowInactiveClassSessions(event.target.checked);
    };

    const activeClassSessionsTitle = 'Class session activity';
    const activeClassSessions = _.sortBy(
        filteredClassSessions.filter(
            (usageEvent) => showInactiveClassSessions || usageEvent.reportingEvents.length > 0
        ),
        (usageEvent) => -Number(usageEvent.class_session.start_time)
    );

    const filterOptions = [
        {
            label: 'Attendance',
            value: 'ATTENDANCE'
        },
        {
            label: 'Talking',
            value: 'IS_TALKING'
        },
        {
            label: 'Public Chat',
            value: 'PUBLIC_CHAT'
        },
        {
            label: 'Private Chat',
            value: 'PRIVATE_CHAT'
        },
        {
            label: 'Raised hand',
            value: 'RAISED_HAND'
        },
        {
            label: 'Focused',
            value: 'FOCUSED'
        },
        {
            label: 'Feedback',
            value: 'FEEDBACK'
        },
        {
            label: 'Reaction',
            value: 'REACTION'
        },
        {
            label: 'Stars',
            value: 'STARS'
        },
        {
            label: 'Error',
            value: 'ERROR'
        }
    ];

    if (userData.loading) {
        return (
            <>
                <Grid container justifyContent="space-between">
                    <Grid item>
                        <Skeleton width={300} height={60} />
                    </Grid>
                    <Grid item>
                        <Skeleton width={150} height={60} />
                    </Grid>
                </Grid>
                <Spacer />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <PaddedSkeleton height={35} />
                <Spacer />
                <Grid container justifyContent="flex-end">
                    <Skeleton width={400} height={50} />
                </Grid>
            </>
        );
    }

    const eventColors = {
        Talking: 'RGB(165, 85, 70)',
        'Hand Raised': 'RGB(213,135,117)',
        Focused: 'RGB(240, 230, 215)',
        Attendance: 'RGB(230, 180, 155)'
    } as any;
    if (!actionFilter.includes('IS_TALKING')) {
        delete eventColors['Talking'];
    }
    if (!actionFilter.includes('RAISED_HAND')) {
        delete eventColors['Hand Raised'];
    }
    if (!actionFilter.includes('FOCUSED')) {
        delete eventColors['Focused'];
    }
    if (!actionFilter.includes('ATTENDANCE')) {
        delete eventColors['Attendance'];
    }

    return (
        <>
            <Table style={{ width: '100%', padding: '0px', margin: '0px' }}>
                <TableBody style={{ width: '100%', padding: '0px', margin: '0px' }}>
                    <TableRow style={{ width: '100%', padding: '0px', margin: '0px' }}>
                        <TableCell style={{ borderBottom: 'none' }}>
                            <UserToolbar searchText={searchText} handleChangeSearch={handleChangeSearch} />
                        </TableCell>
                        <TableCell style={{ borderBottom: 'none' }}>
                            <StyledRoleSelect
                                name="roles"
                                id="demo-multiple-checkbox"
                                multiple
                                value={actionFilter}
                                onChange={(event) => handleActionFilterChange(event)}
                                renderValue={(selected) =>
                                    `${_.isArray(selected) ? selected.length : '0'} ${t('event-types')}`
                                }
                            >
                                {filterOptions.map((filterOption) => (
                                    <MenuItem key={filterOption.value} value={filterOption.value}>
                                        <Checkbox checked={actionFilter.indexOf(filterOption.value) > -1}></Checkbox>
                                        <ListItemText primary={t(filterOption.label)} />
                                    </MenuItem>
                                ))}
                            </StyledRoleSelect>
                        </TableCell>
                        <TableCell style={{ borderBottom: 'none' }}>
                            <ListItemText
                                primary={t('show-inactive-class-sessions')}
                                style={{ display: 'inline-block' }}
                            />
                            <Checkbox
                                onChange={(event) => handleShowInactiveClassSessionsChange(event)}
                                checked={showInactiveClassSessions}
                            ></Checkbox>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
            <Spacer />
            {Object.keys(eventColors).map((eventType) => (
                <div
                    style={{
                        backgroundColor: eventColors[eventType],
                        padding: '4px',
                        display: 'inline-block',
                        marginRight: '8px',
                        fontWeight: 'bold'
                    }}
                >
                    {eventType}
                </div>
            ))}
            <ClassSessionHeatmap
                style={{
                    width: '100%'
                }}
            >
                <h2>{activeClassSessionsTitle}</h2>
                {activeClassSessions!.map((classSession, index) => (
                    <UserTimelineClassSessionRow
                        actionFilter={actionFilter}
                        classSessionData={classSession}
                        key={index}
                        index={index}
                    ></UserTimelineClassSessionRow>
                ))}
            </ClassSessionHeatmap>
        </>
    );
};
